import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { DollarSign, ArrowRight, TrendingUp } from "lucide-react";
import { api } from "@/lib/api";

/**
 * State-grid pay heatmap — FiveThirtyEight-style cartogram, brutalist edition.
 * Each state gets a box roughly in its geographic position, colored by median
 * hourly wage. Empty states get a muted "no data" box. Component returns null
 * entirely when the backend says `enough_data=false` (no empty map ever).
 */

// Rough US-state cartogram grid, 12 cols × 8 rows.
// Each cell = [row, col, abbr]. Empty spots left blank.
const STATE_GRID = [
  ["AK", 0, 0], [/* gap */,,,], , , , , , , , , ["ME", 0, 11],
  ["HI", 1, 0], , , , , , , , , , , ["VT", 1, 10], ["NH", 1, 11],
  , , ["WA", 2, 2], ["ID", 2, 3], ["MT", 2, 4], ["ND", 2, 5], ["MN", 2, 6], ["IL", 2, 7], ["WI", 2, 8], ["MI", 2, 9], ["NY", 2, 10], ["MA", 2, 11],
  , , ["OR", 3, 2], ["UT", 3, 3], ["WY", 3, 4], ["SD", 3, 5], ["IA", 3, 6], ["IN", 3, 7], ["OH", 3, 8], ["PA", 3, 9], ["NJ", 3, 10], ["CT", 3, 11],
  , , ["CA", 4, 2], ["NV", 4, 3], ["CO", 4, 4], ["NE", 4, 5], ["MO", 4, 6], ["KY", 4, 7], ["WV", 4, 8], ["VA", 4, 9], ["MD", 4, 10], ["DE", 4, 11], ["RI", 4, 12],
  , , , ["AZ", 5, 3], ["NM", 5, 4], ["KS", 5, 5], ["AR", 5, 6], ["TN", 5, 7], ["NC", 5, 8], ["SC", 5, 9], ["DC", 5, 10],
  , , , , ["TX", 6, 4], ["OK", 6, 5], ["LA", 6, 6], ["MS", 6, 7], ["AL", 6, 8], ["GA", 6, 9],
  , , , , , , , , , ["FL", 7, 10],
];

// Interpolate red intensity based on where the median falls in [min, max]
function heatColor(value, min, max) {
  if (max === min) return "hsl(0, 85%, 55%)";
  const t = Math.max(0, Math.min(1, (value - min) / (max - min)));
  // Ramp: pale yellow (t=0) → red (t=1)
  const h = 45 - t * 45;                // 45 (yellow) → 0 (red)
  const s = 90;
  const l = 85 - t * 45;                // 85 (light) → 40 (deep)
  return `hsl(${h}, ${s}%, ${l}%)`;
}

function StateCell({ st, data, min, max }) {
  const filled = !!data;
  const bg = filled ? heatColor(data.median, min, max) : "#f3f3f3";
  const label = filled ? `$${Math.round(data.median)}` : "—";
  const textDark = !filled || (data.median - min) / (max - min || 1) < 0.55;
  return (
    <div
      className="border-2 border-black flex flex-col items-center justify-center text-center px-1 py-1 select-none"
      style={{ background: bg, color: textDark ? "#111" : "#fff" }}
      title={filled ? `${st}: median $${data.median.toFixed(2)}/hr · ${data.count} report${data.count === 1 ? "" : "s"}` : `${st}: no data yet`}
      data-testid={`heatmap-state-${st}`}
    >
      <div className="text-[10px] font-black tracking-tight leading-none">{st}</div>
      <div className="text-[11px] font-black tabular-nums leading-none mt-0.5">{label}</div>
    </div>
  );
}

export default function PayHeatmap() {
  const [data, setData] = useState(null);
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const { data } = await api.get("/salaries/state-medians", { params: { nurse_type: "RN" } });
        if (!cancelled) setData(data);
      } catch { /* silent — component hides itself */ }
    })();
    return () => { cancelled = true; };
  }, []);

  // When there isn't enough data yet, show a progress bar instead of the map.
  // Turns the empty state into a growth mechanic that nudges verified nurses to submit.
  if (!data) return null;
  if (!data.enough_data) {
    const submissions = data.total_submissions || 0;
    const needed = data.min_submissions_required || 20;
    const remaining = Math.max(0, needed - submissions);
    const pct = Math.min(100, Math.round((submissions / needed) * 100));
    return (
      <section className="border-t-2 border-black bg-white" data-testid="pay-heatmap-progress-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 py-14">
          <div className="grid lg:grid-cols-12 gap-8 items-center">
            <div className="lg:col-span-7">
              <div className="text-xs uppercase tracking-widest text-[hsl(var(--nh-red))] font-black inline-flex items-center gap-1">
                <TrendingUp className="w-4 h-4" strokeWidth={3} /> Community pay map · Unlocking soon
              </div>
              <h2 className="mt-1 text-4xl sm:text-5xl font-black tracking-tighter">
                The state pay map unlocks at <span className="bg-[hsl(var(--nh-yellow))] px-2 -rotate-1 inline-block">{needed} reports</span>.
              </h2>
              <p className="mt-3 text-base text-black/70 max-w-2xl">
                We&apos;re building the first anonymous, verified-nurse pay map for every U.S. state.
                It&apos;ll unlock automatically once {needed} verified nurses have shared their compensation —
                a small privacy threshold so no single respondent can be identified.
              </p>
              <div className="mt-6">
                <div className="flex items-baseline justify-between text-sm">
                  <span className="font-black">{submissions.toLocaleString()} / {needed.toLocaleString()} reports</span>
                  <span className="text-black/60 tabular-nums" data-testid="heatmap-progress-remaining">{remaining} more to unlock</span>
                </div>
                <div className="mt-2 h-4 border-2 border-black bg-white overflow-hidden" data-testid="heatmap-progress-bar">
                  <div
                    className="h-full bg-[hsl(var(--nh-red))] transition-all duration-500 ease-out"
                    style={{ width: `${pct}%` }}
                    aria-valuenow={pct} aria-valuemin={0} aria-valuemax={100} role="progressbar"
                  />
                </div>
                <div className="mt-1 text-[11px] uppercase tracking-widest text-black/50">{pct}% of the way there</div>
              </div>
              <div className="mt-5 flex flex-wrap gap-3">
                <Link to="/salaries" data-testid="heatmap-progress-cta">
                  <Button className="bg-black hover:bg-black/90 text-white rounded-none border-2 border-black h-11 px-5 nh-hover-push">
                    <DollarSign className="w-4 h-4 mr-1" /> Add your salary — help unlock it
                  </Button>
                </Link>
              </div>
            </div>
            <div className="lg:col-span-5">
              <div className="bg-[hsl(var(--nh-yellow))] nh-brutal p-6">
                <div className="text-xs uppercase tracking-widest font-black">How it works</div>
                <ul className="mt-3 space-y-2 text-sm">
                  <li className="flex items-start gap-2"><span className="font-black w-5 shrink-0">1.</span> Verified nurses share their state, license, specialty, hourly wage, and differentials — anonymously.</li>
                  <li className="flex items-start gap-2"><span className="font-black w-5 shrink-0">2.</span> Once we have {needed}+ reports across 5+ states, the pay map goes live for everyone.</li>
                  <li className="flex items-start gap-2"><span className="font-black w-5 shrink-0">3.</span> You always keep control — resubmit any time to update your entry.</li>
                </ul>
              </div>
            </div>
          </div>
        </div>
      </section>
    );
  }

  // Build a lookup {state: {median, count}}
  const meds = data.medians;
  const min = data.min_median;
  const max = data.max_median;

  // Prep grid — 8 rows × 12 cols
  const rows = Array.from({ length: 8 }, () => Array(13).fill(null));
  for (const entry of STATE_GRID) {
    if (!entry) continue;
    const [st, r, c] = entry;
    if (typeof r === "number" && typeof c === "number") rows[r][c] = st;
  }

  return (
    <section className="border-t-2 border-black bg-white" data-testid="pay-heatmap-section">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 py-16">
        <div className="mb-8 flex items-end justify-between flex-wrap gap-4">
          <div>
            <div className="text-xs uppercase tracking-widest text-[hsl(var(--nh-red))] font-black inline-flex items-center gap-1">
              <TrendingUp className="w-4 h-4" strokeWidth={3} /> Anonymous · Verified nurses
            </div>
            <h2 className="mt-1 text-4xl sm:text-5xl font-black tracking-tighter">RN hourly pay by state.</h2>
            <p className="mt-2 text-sm text-black/70 max-w-2xl">
              Median hourly wage for Registered Nurses, sourced from anonymous submissions by verified U.S. nurses.
              &nbsp;<span className="font-black text-black">{data.total_submissions.toLocaleString()}</span> reports across{" "}
              <span className="font-black text-black">{data.states_with_data}</span> states.
            </p>
          </div>
          <Link to="/salaries" data-testid="heatmap-cta">
            <Button className="bg-black hover:bg-black/90 text-white rounded-none border-2 border-black h-11 px-5 nh-hover-push">
              <DollarSign className="w-4 h-4 mr-1" /> Explore salary map <ArrowRight className="w-4 h-4 ml-1" />
            </Button>
          </Link>
        </div>

        {/* Grid */}
        <div className="grid gap-1" style={{ gridTemplateColumns: "repeat(13, minmax(0,1fr))" }} data-testid="heatmap-grid">
          {rows.map((row, ri) => row.map((st, ci) => (
            <div key={`${ri}-${ci}`} className="aspect-square min-h-[42px]">
              {st ? <StateCell st={st} data={meds[st]} min={min} max={max} /> : null}
            </div>
          )))}
        </div>

        {/* Legend */}
        <div className="mt-5 flex flex-wrap items-center gap-3 text-xs">
          <div className="text-black/60 font-black uppercase tracking-widest">Legend:</div>
          <div className="flex items-center gap-1">
            <div className="w-6 h-6 border-2 border-black" style={{ background: heatColor(min, min, max) }} />
            <span className="text-black/80">${Math.round(min)}/hr</span>
          </div>
          <div className="flex items-center gap-1">
            <div className="w-6 h-6 border-2 border-black" style={{ background: heatColor((min + max) / 2, min, max) }} />
            <span className="text-black/80">${Math.round((min + max) / 2)}/hr</span>
          </div>
          <div className="flex items-center gap-1">
            <div className="w-6 h-6 border-2 border-black" style={{ background: heatColor(max, min, max) }} />
            <span className="text-black/80">${Math.round(max)}/hr</span>
          </div>
          <div className="flex items-center gap-1">
            <div className="w-6 h-6 border-2 border-black bg-[#f3f3f3]" />
            <span className="text-black/60">No data yet</span>
          </div>
        </div>
      </div>
    </section>
  );
}
